var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useState, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import styled from 'styled-components';
import { ContextualHelpNotification } from '../ContextualHelpNotification';
import { FormFieldHelper } from '../FormFieldHelper';
import { Space } from '../Space';
import { extractFormFieldHelperProps } from './utils';
import { CharacterCounter } from '../CharacterCounter';
import { formTextBackgroundStyles } from './context';
const InlineContextualHelp = styled(ContextualHelpNotification) `
  ${({ theme: { space } }) => `
    display: inline-flex;
    width: fit-content;
    height: 44px;
    align-items: center;
    margin-left: ${space.xs};
    padding-top: 3px;
  `}
`;
const StyledCharacterCounter = styled(CharacterCounter) `
  ${({ $isDisabled, theme: { colors } }) => `
    color: ${colors['slate-5']};
    ${$isDisabled ? `color: ${colors['periwinkle-500']};` : ''}
    ${$isDisabled ? 'cursor: not-allowed;' : ''}
  `}
`;
const CharacterCounterWrapper = styled.div `
  ${({ theme: { space }, $marginRight }) => {
    return `
      position: absolute;
      bottom: ${space.xxs};
      padding: 1px;
      margin-right: ${$marginRight}px;
      border-radius: 1px;
      right: 0;
    `;
}}

  ${formTextBackgroundStyles}
`;
export const useFormText = (_a) => {
    var _b;
    var { isHovered } = _a, props = __rest(_a, ["isHovered"]);
    const safeCharacterCounter = props.variant === 'input' &&
        (props.adornment || props.inputType === 'number')
        ? {}
        : props.characterCounter;
    const { variant, labelContent, errorNotification, className, internalRef } = props;
    const { current: staticId } = useRef(uuidv4());
    const { current: charCounterId } = useRef(`field-counter-${uuidv4()}`);
    const characterCounterRef = useRef(null);
    const [stackedDescribedBy, setStackedDescribedBy] = useState();
    const formFieldHelperProps = useMemo(() => extractFormFieldHelperProps(props), [props]);
    const formFieldHelper = useMemo(() => (React.createElement(Space, { mt: "xs" },
        React.createElement(FormFieldHelper, Object.assign({ onDescribedByChange: setStackedDescribedBy }, formFieldHelperProps)))), [formFieldHelperProps, setStackedDescribedBy]);
    const inlineContextualHelp = useMemo(() => {
        if (!props.contextualHelp ||
            props.contextualHelp.variant !== 'inline' ||
            !props.contextualHelp.content)
            return null;
        const { content, customId } = props.contextualHelp;
        const helpId = customId || `help-id-${staticId}`;
        return {
            id: helpId,
            element: React.createElement(InlineContextualHelp, { id: helpId, content: content }),
        };
    }, [props.contextualHelp, staticId]);
    const displayCounter = useMemo(() => {
        return !!(charCounterId &&
            charCounterId.length &&
            typeof props.maxLength === 'number' &&
            props.maxLength > 0 &&
            (safeCharacterCounter === null || safeCharacterCounter === void 0 ? void 0 : safeCharacterCounter.show));
    }, [charCounterId, safeCharacterCounter === null || safeCharacterCounter === void 0 ? void 0 : safeCharacterCounter.show, props.maxLength]);
    const characterCounter = useMemo(() => {
        var _a, _b;
        return (React.createElement(CharacterCounterWrapper, { "$variant": variant, ref: characterCounterRef, "$isHovered": isHovered || false, "$isDisabled": props.disabled || false, "$marginRight": Math.ceil((((_a = internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) === null || _a === void 0 ? void 0 : _a.offsetWidth) || 0) -
                (((_b = internalRef === null || internalRef === void 0 ? void 0 : internalRef.current) === null || _b === void 0 ? void 0 : _b.clientWidth) || 0) +
                8) },
            React.createElement(StyledCharacterCounter, { id: (safeCharacterCounter === null || safeCharacterCounter === void 0 ? void 0 : safeCharacterCounter.customId) || charCounterId, charCount: props.value.length, maxLength: props.maxLength || 0, hideAppendedText: true, "$isDisabled": props.disabled || false })));
    }, [
        variant,
        isHovered,
        props.disabled,
        safeCharacterCounter === null || safeCharacterCounter === void 0 ? void 0 : safeCharacterCounter.customId,
        props.value.length,
        props.maxLength,
        charCounterId,
    ]);
    return {
        variant,
        labelContent,
        className,
        describedBy: `${stackedDescribedBy || ''}${(safeCharacterCounter === null || safeCharacterCounter === void 0 ? void 0 : safeCharacterCounter.show) || (safeCharacterCounter === null || safeCharacterCounter === void 0 ? void 0 : safeCharacterCounter.customId)
            ? ` ${(safeCharacterCounter === null || safeCharacterCounter === void 0 ? void 0 : safeCharacterCounter.customId) || charCounterId}`
            : ''}`,
        formTextId: props.id || `mui-text-field-${staticId}`,
        hasError: !!(errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.message) || !!(errorNotification === null || errorNotification === void 0 ? void 0 : errorNotification.customId),
        isDisabled: !!props.disabled,
        formFieldHelper,
        inlineContextualHelp: (inlineContextualHelp === null || inlineContextualHelp === void 0 ? void 0 : inlineContextualHelp.element) || null,
        characterCounter: displayCounter ? characterCounter : undefined,
        characterCounterWidth: 
        // the + 15 accounts for 8px padding to the right of the character counter
        // and gives an extra 2px padding to the left of it
        Math.ceil(((_b = characterCounterRef.current) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect().width) || 0) + 15,
    };
};
//# sourceMappingURL=useFormText.js.map